import { useEffect, useState } from 'react';
import { CONFIG } from '../config';
import { getIdToken } from '../auth';
import { listUsers } from '../api';

/** yyyymmdd 8 桁 → yyyy-mm-dd (input[type=date] 用) */
function ymdToInput(s: string): string {
  if (!/^\d{8}$/.test(s)) return '';
  return `${s.slice(0, 4)}-${s.slice(4, 6)}-${s.slice(6, 8)}`;
}
/** input[type=date] の yyyy-mm-dd → yyyymmdd */
function inputToYmd(s: string): string {
  return s.replace(/-/g, '');
}
/** N 日前 (ローカル日付) の yyyymmdd */
function daysAgoYmd(days: number): string {
  const d = new Date();
  d.setDate(d.getDate() - days);
  return fmtYmd(d);
}
function fmtYmd(d: Date): string {
  return `${d.getFullYear()}${String(d.getMonth() + 1).padStart(2, '0')}${String(
    d.getDate(),
  ).padStart(2, '0')}`;
}
/**
 * offset=0 → 今月 (1日〜今日)
 * offset=1 → 先月 (1日〜末日)
 * offset=2 → 先々月 (1日〜末日)
 * 戻り値: [start, end] (yyyymmdd)
 */
function monthRange(offset: number): [string, string] {
  const today = new Date();
  const first = new Date(today.getFullYear(), today.getMonth() - offset, 1);
  const lastOfMonth = new Date(
    first.getFullYear(),
    first.getMonth() + 1,
    0,
  );
  const end = offset === 0 ? today : lastOfMonth;
  return [fmtYmd(first), fmtYmd(end)];
}

export default function Export() {
  const [prefixes, setPrefixes] = useState<string[]>([]);
  const [prefix, setPrefix] = useState<string>('');
  const [start, setStart] = useState<string>(ymdToInput(daysAgoYmd(7)));
  const [end, setEnd] = useState<string>(ymdToInput(daysAgoYmd(0)));
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  // プリフィックスをテストユーザー一覧から集める (uniq)
  useEffect(() => {
    (async () => {
      try {
        const { users } = await listUsers();
        const uniq = Array.from(
          new Set(users.map((u) => u.prefix).filter(Boolean)),
        ).sort();
        setPrefixes(uniq);
      } catch (e) {
        setErr(`プリフィックス一覧の取得に失敗: ${e}`);
      }
    })();
  }, []);

  function pickMonth(offset: number, label: string) {
    const [s, e] = monthRange(offset);
    setStart(ymdToInput(s));
    setEnd(ymdToInput(e));
    setErr(null);
    void label; // for debug/analytics
  }

  async function download() {
    setErr(null);
    if (!prefix.trim()) {
      setErr('プリフィックスを選択してください');
      return;
    }
    const s = inputToYmd(start);
    const e = inputToYmd(end);
    if (!/^\d{8}$/.test(s) || !/^\d{8}$/.test(e)) {
      setErr('開始・終了日を指定してください');
      return;
    }
    if (s > e) {
      setErr('開始日は終了日以前にしてください');
      return;
    }
    setBusy(true);
    try {
      const token = await getIdToken();
      const url = new URL(`${CONFIG.apiBase}/v1/export/hourly-csv`);
      url.searchParams.set('prefix', prefix.trim());
      url.searchParams.set('start', s);
      url.searchParams.set('end', e);
      const res = await fetch(url.toString(), {
        method: 'GET',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!res.ok) {
        const txt = await res.text();
        throw new Error(`${res.status} ${res.statusText}: ${txt.slice(0, 200)}`);
      }
      const blob = await res.blob();
      const filename = `hourly_${prefix.trim()}_${s}-${e}.csv`;
      const dlUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = dlUrl;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(dlUrl);
    } catch (e) {
      setErr(String(e));
    } finally {
      setBusy(false);
    }
  }

  const pillStyle = (active: boolean): React.CSSProperties => ({
    padding: '6px 14px',
    borderRadius: 999,
    border: active ? '1px solid #4A90E2' : '1px solid #444',
    background: active ? '#4A90E2' : 'transparent',
    color: active ? '#fff' : 'inherit',
    cursor: 'pointer',
    fontSize: 13,
  });

  return (
    <div>
      <h2>CSV エクスポート</h2>
      <div className="card">
        <h3>時刻別アクティブ台数 CSV</h3>
        <p className="muted">
          プリフィックスで始まる端末について、期間内の各日 × 時刻ごとの
          アクティブ端末数と、その日の総回数を CSV でダウンロードします。
          <br />
          （データ源は HOUR_AGG、保持は約 30 日。最大 62 日まで指定可）
        </p>
        {err && <div className="error">{err}</div>}

        <div style={{ marginTop: 16 }}>
          <label style={{ display: 'block', marginBottom: 6 }}>
            プリフィックス
          </label>
          {prefixes.length === 0 ? (
            <p className="muted" style={{ margin: 0 }}>
              (プリフィックス一覧を読み込み中…)
            </p>
          ) : (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
              {prefixes.map((p) => (
                <button
                  key={p}
                  style={pillStyle(prefix === p)}
                  onClick={() => setPrefix(p)}
                >
                  {p}
                </button>
              ))}
            </div>
          )}
        </div>

        <div style={{ marginTop: 20 }}>
          <label style={{ display: 'block', marginBottom: 6 }}>期間</label>
          <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
            <button style={pillStyle(false)} onClick={() => pickMonth(0, '今月')}>
              今月
            </button>
            <button style={pillStyle(false)} onClick={() => pickMonth(1, '先月')}>
              先月
            </button>
            <button
              style={pillStyle(false)}
              onClick={() => pickMonth(2, '先々月')}
            >
              先々月
            </button>
          </div>
          <div className="row" style={{ gap: 20, flexWrap: 'wrap' }}>
            <div className="col">
              <label>開始日</label>
              <input
                type="date"
                value={start}
                onChange={(e) => setStart(e.target.value)}
              />
            </div>
            <div className="col">
              <label>終了日</label>
              <input
                type="date"
                value={end}
                onChange={(e) => setEnd(e.target.value)}
              />
            </div>
          </div>
        </div>

        <div style={{ marginTop: 20 }}>
          <button disabled={busy} onClick={download}>
            {busy ? '生成中…' : 'ダウンロード'}
          </button>
        </div>
      </div>
    </div>
  );
}
